import { defineStore } from "pinia";
import dayjs from "dayjs";
import router from "@/router";
import FetchService from "@/services/fetch.service";
import { useConferenceStore } from "@/stores/teacher/conference";
import { useSchoolStore } from "@/stores/school";
import { useAuthenticationStore } from "@/stores/authentication";
import { sort } from "@/utils/sort";
import { formatStudentLimitMessage, isStudentLimitError } from "@/utils/studentLimitError";
import type { ConferenceDay, GetConference } from "@/api_types/teacher";
type Break = {
  start_time: Date;
  end_time: Date;
  has_teachers: boolean;
  has_teams: boolean;
};

// Helper function to check if user is district
const isDistrictUser = () => {
  const authStore = useAuthenticationStore();
  const schoolStore = useSchoolStore();
  return authStore.jwtData?.authentication_type === "district" || schoolStore.isDistrict();
};

// Helper function to check if a school is a district school
const isDistrictSchool = (isDistrict: string | boolean | undefined): boolean => {
  if (isDistrict === undefined) return false;
  return isDistrict === "yes" || isDistrict === true || isDistrict === "true";
};

const getSteps = () => {
  const baseSteps = [
    {
      title: "Event",
      component: "EventTab",
      finished() {
        const w: any = useWizard();
        return !!(w.name?.length && w.name.length >= 3);
      },
      icon: "mdi-calendar-blank",
      message: "Event Name needs to have at least 3 characters"
    },
    {
      title: "Days",
      component: "DaysTab",
      finished() {
        const w: any = useWizard();
        return w.days.length === w.eventDays;
      },
      icon: "mdi-calendar-today",
      message: "Make sure to add days as the specified number"
    },
    {
      title: "Schedules",
      component: "SchedulesTab",
      finished() {
        const w: any = useWizard();
        return w.allTeachers || w.allTeams;
      },
      icon: "mdi-cog",
      message: "At least one of the options need to be selected"
    },
    {
      title: "Breaks",
      component: "BreaksTab",
      finished() {
        const w: any = useWizard();
        // Find the Schedules step by component name
        const schedulesStep = w.steps.find((s: any) => s.component === "SchedulesTab");
        return schedulesStep ? schedulesStep.finished() : true;
      },
      icon: "mdi-coffee",
      message: ""
    }
  ];

  // Add Schools step at the beginning for district users
  if (isDistrictUser()) {
    baseSteps.unshift({
      title: "Schools",
      component: "SchoolsTab",
      finished() {
        const w: any = useWizard();
        return w.selectAllSchools || (w.selectedSchools && w.selectedSchools.length > 0);
      },
      icon: "mdi-school",
      message: "Please select at least one school or select all schools"
    });
  }

  return baseSteps;
};

export type EventDay = {
  start_time: Date;
  end_time: Date;
  interval: number;
  breaks: Break[];
};

export type SubmitMessage = {
  status: "success" | "loading" | "error";
  message: string;
};
export const useWizard = defineStore("school/wizard", {
  state: () => {
    const cSteps = getSteps().map((i) => ({ ...i }));
    return {
      step: cSteps[0],
      steps: cSteps,
      name: "",
      eventDays: 1,
      days: [] as EventDay[],
      newDay: {
        start_time: dayjs().set("hours", 8).set("minutes", 0).toDate(),
        end_time: dayjs().set("hours", 17).set("minutes", 0).toDate(),
        interval: 15
      },
      allTeachers: false,
      allTeams: false,
      loading: false,
      showModal: false,
      submitMessages: [] as SubmitMessage[],
      error: false,
      savedDays: [] as { index: number; day: ConferenceDay }[],
      // Schools step state
      selectAllSchools: false,
      selectedSchools: [] as number[]
    };
  },
  actions: {
    saveDay() {
      this.days.push({ ...this.newDay, breaks: [] });
      this.newDay = {
        start_time: dayjs().set("hours", 8).set("minutes", 0).toDate(),
        end_time: dayjs().set("hours", 17).set("minutes", 0).toDate(),
        interval: 15
      };
    },
    async submit() {
      this.loading = true;
      this.showModal = true;
      this.submitMessages = [];

      const confStore = useConferenceStore();
      const schoolStore = useSchoolStore();
      const days = sort(this.days, (i) => i.start_time.toISOString());
      const firstDay = days[0];

      // Determine which schools to process
      let schoolsToProcess: number[] = [];
      if (isDistrictUser() && this.selectAllSchools) {
        // Fetch all schools for district
        const { useDistrictStore } = await import("@/stores/district");
        const districtStore = useDistrictStore();
        const allSchools = await districtStore.fetchSchools();
        // Filter out district schools (is_district === "yes" or is_district === true)
        const nonDistrictSchools = allSchools.filter(
          (s) => !isDistrictSchool(s.is_district as string | boolean)
        );
        // Include the current school if it exists and is not a district school
        if (schoolStore.school?.school_id) {
          const currentSchoolIsDistrict = isDistrictSchool(
            schoolStore.school.is_district as string | boolean
          );
          if (!currentSchoolIsDistrict) {
            schoolsToProcess = [
              schoolStore.school.school_id,
              ...nonDistrictSchools.map((s) => s.school_id)
            ];
          } else {
            schoolsToProcess = nonDistrictSchools.map((s) => s.school_id);
          }
        } else {
          schoolsToProcess = nonDistrictSchools.map((s) => s.school_id);
        }
      } else if (isDistrictUser() && this.selectedSchools.length > 0) {
        schoolsToProcess = this.selectedSchools;
      } else {
        // Non-district user or no schools selected - use current school
        if (schoolStore.school_id) {
          schoolsToProcess = [schoolStore.school_id];
        } else if (schoolStore.school?.school_id) {
          schoolsToProcess = [schoolStore.school.school_id];
        } else {
          schoolsToProcess = [];
        }
      }

      if (schoolsToProcess.length === 0) {
        this.submitMessages.push({
          status: "error",
          message: "No schools selected"
        });
        this.error = true;
        this.loading = false;
        return;
      }

      const eventData = {
        conference_name: this.name,
        open_tstamp: dayjs(firstDay.start_time)
          .subtract(2, "weeks")
          .set("hours", 8)
          .set("minutes", 0)
          .set("seconds", 0)
          .format(`YYYY-MM-DD HH:mm:ss`),
        closed_tstamp: dayjs(firstDay.start_time)
          .subtract(1, "days")
          .set("hours", 12)
          .set("minutes", 0)
          .set("seconds", 0)
          .format(`YYYY-MM-DD HH:mm:ss`)
      };

      let allEventIds: number[] = [];
      let hasError = false;

      // Store the original school_id to restore it later
      const originalSchoolId = schoolStore.school_id;

      // Process each school
      for (let schoolIndex = 0; schoolIndex < schoolsToProcess.length; schoolIndex++) {
        const schoolId = schoolsToProcess[schoolIndex];
        let event_id = 0;

        try {
          // For district users, set the school_id in the store so it's included in the x-school-id header
          // This must be set before any API calls so the authHeader function picks it up
          // The school_id will be used by authHeader() for all subsequent API calls in this iteration
          if (isDistrictUser() && schoolId) {
            schoolStore.school_id = schoolId;
          }

          this.submitMessages.push({
            status: "loading",
            message: `Creating event for school ${schoolId}${schoolsToProcess.length > 1 ? ` (${schoolIndex + 1}/${schoolsToProcess.length})` : ""}`
          });

          // Create conference - the school_id will be in the x-school-id header for district users
          const event = (await FetchService.fetchData(
            `/conferences`,
            "post",
            eventData,
            false,
            undefined,
            false
          )) as GetConference;
          if (event?.conference_id) {
            event_id = event.conference_id;
            allEventIds.push(event_id);
            this.submitMessages.at(-1)!.status = "success";

            // Add to conference store
            confStore.conferences.push(event);
            confStore.setCurrentConferences(confStore.current_conferences.concat(event));

            for (let i = 0; i < days.length; i++) {
              const day = days[i];
              this.submitMessages.push({
                status: "loading",
                message: `Adding event day ${i + 1} for school ${schoolId}`
              });
              const end = dayjs(day.end_time);
              const eventDay = (await FetchService.fetchData("/conference-days", "post", {
                start: dayjs(day.start_time).set("seconds", 0).format(`YYYY-MM-DD HH:mm:ss`),
                end: dayjs(day.start_time)
                  .set("hours", end.hour())
                  .set("minutes", end.minute())
                  .set("seconds", 0)
                  .format(`YYYY-MM-DD HH:mm:ss`),
                time_interval: day.interval,
                conference_id: event.conference_id,
                meeting_types: {
                  in_person: true,
                  virtual: false,
                  callback: false,
                  phone: false,
                  virtual_type: "url"
                }
              })) as ConferenceDay;
              if (eventDay?.conference_days_id) {
                this.submitMessages.at(-1)!.status = "success";
                confStore.updatedConference(event.conference_id, {
                  conference_days: [
                    ...(confStore.getById(event.conference_id)?.conference_days || []),
                    eventDay
                  ]
                });
                // add teachers/teams
                let m = "Adding ";
                if (this.allTeachers) m += "teachers" + (this.allTeams ? " and teams" : "");
                else m += "teams";
                m += ` for day ${i + 1} (school ${schoolId})`;
                this.submitMessages.push({
                  status: "loading",
                  message: m
                });
                const res = await FetchService.fetchData(
                  `/conference-days/entities-bulk/${eventDay.conference_days_id}?teachers=${this.allTeachers}&teams=${this.allTeams}`,
                  "post"
                );

                if (res?.success) {
                  this.submitMessages.at(-1)!.status = "success";
                  if (day.breaks.length) {
                    // add breaks
                    this.submitMessages.push({
                      status: "loading",
                      message: "Adding Breaks for day " + (i + 1)
                    });
                    const breaks = day.breaks.map((i) => {
                      const start = dayjs(i.start_time);
                      const end = dayjs(i.end_time);
                      return {
                        start_time: dayjs(day.start_time)
                          .set("hours", start.hour())
                          .set("minutes", start.minute())
                          .set("seconds", 0)
                          .format(`YYYY-MM-DD HH:mm:ss`),
                        end_time: dayjs(day.start_time)
                          .set("hours", end.hour())
                          .set("minutes", end.minute())
                          .set("seconds", 0)
                          .format(`YYYY-MM-DD HH:mm:ss`),
                        conference_days_id: eventDay.conference_days_id,
                        has_teachers: i.has_teachers,
                        has_teams: i.has_teams
                      };
                    });
                    let breaksSuccess = true;
                    for (let i = 0; i < breaks.length; i++) {
                      const breakRes = await FetchService.fetchData(
                        `/conference-breaks`,
                        "post",
                        breaks[i]
                      );
                      if (!breakRes?.success) {
                        breaksSuccess = false;
                        break;
                      }
                    }

                    if (breaksSuccess) {
                      this.submitMessages.at(-1)!.status = "success";
                    } else {
                      this.submitMessages.at(-1)!.status = "error";
                      throw `Error while trying to add breaks for school ${schoolId}`;
                    }
                  }
                } else {
                  this.submitMessages.at(-1)!.status = "error";
                  throw `Error while trying to add teachers/teams for school ${schoolId}`;
                }
              } else {
                this.submitMessages.at(-1)!.status = "error";
                throw `Error while trying to add event day for school ${schoolId}`;
              }
            }
          } else {
            const currentMessage = this.submitMessages.at(-1)!;
            currentMessage.status = "error";
            if (isStudentLimitError(event)) {
              currentMessage.message = formatStudentLimitMessage(event);
              hasError = true;
              continue;
            }
            throw `Error while trying to create event for school ${schoolId}`;
          }
        } catch (error: any) {
          hasError = true;
          this.submitMessages.push({
            status: "error",
            message: error?.message || error || `Error processing school ${schoolId}`
          });
          // Clean up: delete the event if it was created
          if (event_id) {
            try {
              await confStore.deleteConference(event_id);
            } catch (deleteError) {
              console.error("Error deleting conference:", deleteError);
            }
          }
        }
      }

      // Restore the original school_id for district users
      if (isDistrictUser()) {
        schoolStore.school_id = originalSchoolId;
      }

      // Final message
      if (hasError) {
        this.error = true;
        this.submitMessages.push({
          status: "error",
          message: "Some events failed to be created. Please check the messages above."
        });
      } else {
        this.submitMessages.push({
          status: "success",
          message: `Event${schoolsToProcess.length > 1 ? "s" : ""} Added Successfully for ${schoolsToProcess.length} school${schoolsToProcess.length > 1 ? "s" : ""}`
        });
      }
      this.loading = false;
    },
    close() {
      this.showModal = false;
      if (!this.error) {
        this.$reset();
        router.push({
          name: "school-events"
        });
      }
    }
  }
});

export default useWizard;
